import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AddProspectModal from '../../../AddProspectModal';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Prospects() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  
  // Sorting
  const { items: sortedProspects, requestSort, getSortIndicator } = useSortableTable(prospects, { key: 'date_created', direction: 'desc' });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [loadError, setLoadError] = useState('');
  const columnMenuRef = useRef(null);

  // Column visibility state - load from API
  const [visibleColumns, setVisibleColumns] = useState({
    prospectName: true,
    contact: true,
    phone: true,
    email: true,
    doorLocation: true,
    leadSource: true,
    quoteAmount: true,
    status: true,
    dateCreated: true,
    lastContact: false,
  });

  // Available columns configuration
  const columns = [
    { key: 'prospectName', label: 'Company/Prospect' },
    { key: 'contact', label: 'Contact' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'doorLocation', label: 'Door Location' },
    { key: 'leadSource', label: 'Lead Source' },
    { key: 'quoteAmount', label: 'Quote Amount' },
    { key: 'status', label: 'Status' },
    { key: 'dateCreated', label: 'Date Created' },
    { key: 'lastContact', label: 'Last Contact' },
  ];

  useEffect(() => {
    fetchProspects();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/prospects');
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/prospects', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  // Save column visibility to API whenever it changes
  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProspects = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const response = await apiFetch('/api/prospects');
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load prospects');
      }
      setProspects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching prospects:', error);
      setLoadError(error.message || 'Failed to load prospects');
      setProspects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProspect = () => {
    setShowAddModal(true);
  };

  const handleProspectAdded = () => {
    fetchProspects();
    setShowAddModal(false);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Filter prospects based on search term, status, and source
  const filteredProspects = sortedProspects.filter(prospect => {
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch = !search || [
      prospect.prospect_name,
      prospect.contact_name,
      prospect.email,
      prospect.phone,
      prospect.door_location
    ].some(value => String(value || '').toLowerCase().includes(search));
    
    const matchesStatus = !statusFilter || prospect.status === statusFilter;
    const matchesSource = !sourceFilter || prospect.lead_source === sourceFilter;
    
    return matchesSearch && matchesStatus && matchesSource;
  });

  const formatCurrency = (amount) => {
    if (!amount) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Quoted': 'bg-blue-100 text-blue-800',
      'Following Up': 'bg-yellow-100 text-yellow-800',
      'Accepted': 'bg-green-100 text-green-800',
      'Declined': 'bg-red-100 text-red-800',
      'Expired': 'bg-gray-100 text-gray-800',
      'Converted': 'bg-purple-100 text-purple-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader 
        title="Prospects"
        subtitle={`${filteredProspects.length} total`}
        backButton
        onBack={() => navigate('/crm')}
        backTitle="Back to CRM"
        sticky
        actionButton={
          <button
            onClick={handleAddProspect}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add Prospect
          </button>
        }
      />

      {/* Search and Column Toggle */}
      <div className="px-4 py-3">
        {loadError && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
            Prospect API error: {loadError}
          </div>
        )}

        <div className="mb-3 flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search prospects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
          />
          
          {/* Column Visibility Button */}
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition flex items-center space-x-1"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>

            {/* Dropdown Menu */}
            {showColumnMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-600 mb-2 px-2">Show Columns</div>
                  {columns.map(column => (
                    <label
                      key={column.key}
                      className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                        className="mr-2 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-2 mb-2">
          <button
            onClick={() => { setStatusFilter(''); setSourceFilter(''); }}
            className={`px-3 py-1 text-xs rounded ${!statusFilter && !sourceFilter ? 'bg-teal-100 text-teal-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All ({prospects.length})
          </button>
          <button
            onClick={() => setStatusFilter('Quoted')}
            className={`px-3 py-1 text-xs rounded ${statusFilter === 'Quoted' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Quoted
          </button>
          <button
            onClick={() => setStatusFilter('Following Up')}
            className={`px-3 py-1 text-xs rounded ${statusFilter === 'Following Up' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Following Up
          </button>
          <button
            onClick={() => setStatusFilter('Accepted')}
            className={`px-3 py-1 text-xs rounded ${statusFilter === 'Accepted' ? 'bg-green-100 text-green-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Accepted
          </button>
          <button
            onClick={() => setStatusFilter('Declined')}
            className={`px-3 py-1 text-xs rounded ${statusFilter === 'Declined' ? 'bg-red-100 text-red-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Declined
          </button>
          <button
            onClick={() => setStatusFilter('Converted')}
            className={`px-3 py-1 text-xs rounded ${statusFilter === 'Converted' ? 'bg-purple-100 text-purple-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Converted
          </button>
        </div>
      </div>

      {/* Table Container - scrollable */}
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            ) : filteredProspects.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No prospects found</p>
                <p className="text-sm mt-2">Click "Add Prospect" to create your first lead</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
                  <tr>
                    {visibleColumns.prospectName && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('prospect_name')}
                      >
                        Company/Prospect{getSortIndicator('prospect_name')}
                      </th>
                    )}
                    {visibleColumns.contact && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('contact_name')}
                      >
                        Contact{getSortIndicator('contact_name')}
                      </th>
                    )}
                    {visibleColumns.phone && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('phone')}
                      >
                        Phone{getSortIndicator('phone')}
                      </th>
                    )}
                    {visibleColumns.email && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('email')}
                      >
                        Email{getSortIndicator('email')}
                      </th>
                    )}
                    {visibleColumns.doorLocation && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('door_location')}
                      >
                        Door Location{getSortIndicator('door_location')}
                      </th>
                    )}
                    {visibleColumns.leadSource && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('lead_source')}
                      >
                        Lead Source{getSortIndicator('lead_source')}
                      </th>
                    )}
                    {visibleColumns.quoteAmount && (
                      <th 
                        className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('quote_amount')}
                      >
                        Quote Amount{getSortIndicator('quote_amount')}
                      </th>
                    )}
                    {visibleColumns.status && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('status')}
                      >
                        Status{getSortIndicator('status')}
                      </th>
                    )}
                    {visibleColumns.dateCreated && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('date_created')}
                      >
                        Date Created{getSortIndicator('date_created')}
                      </th>
                    )}
                    {visibleColumns.lastContact && (
                      <th 
                        className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none"
                        onClick={() => requestSort('last_contact')}
                      >
                        Last Contact{getSortIndicator('last_contact')}
                      </th>
                    )}
                    <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProspects.map((prospect, index) => (
                    <tr
                      key={prospect.id}
                      className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                      onClick={() => navigate(`/crm/prospects/${prospect.id}`)}
                    >
                      {visibleColumns.prospectName && (
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{prospect.prospect_name}</div>
                        </td>
                      )}
                      {visibleColumns.contact && (
                        <td className="px-3 py-2 text-gray-700">{prospect.contact_name || '-'}</td>
                      )}
                      {visibleColumns.phone && (
                        <td className="px-3 py-2 text-gray-700">{prospect.phone || '-'}</td>
                      )}
                      {visibleColumns.email && (
                        <td className="px-3 py-2 text-gray-700">{prospect.email || '-'}</td>
                      )}
                      {visibleColumns.doorLocation && (
                        <td className="px-3 py-2 text-gray-700">{prospect.door_location || '-'}</td>
                      )}
                      {visibleColumns.leadSource && (
                        <td className="px-3 py-2 text-gray-700">{prospect.lead_source || '-'}</td>
                      )}
                      {visibleColumns.quoteAmount && (
                        <td className="px-3 py-2 text-right font-medium text-gray-900">{formatCurrency(prospect.quote_amount)}</td>
                      )}
                      {visibleColumns.status && (
                        <td className="px-3 py-2">{getStatusBadge(prospect.status)}</td>
                      )}
                      {visibleColumns.dateCreated && (
                        <td className="px-3 py-2 text-gray-700">{formatDate(prospect.date_created)}</td>
                      )}
                      {visibleColumns.lastContact && (
                        <td className="px-3 py-2 text-gray-700">{formatDate(prospect.last_contact)}</td>
                      )}
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/crm/prospects/${prospect.id}`);
                          }}
                          className="text-teal-600 hover:text-teal-900 font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Count at bottom */}
          {!loading && filteredProspects.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
              Showing {filteredProspects.length} of {prospects.length} prospects
            </div>
          )}
        </div>
      </div>

      {/* Add Prospect Modal */}
      {showAddModal && (
        <AddProspectModal
          onClose={() => setShowAddModal(false)}
          onProspectAdded={handleProspectAdded}
        />
      )}
    </div>
  );
}

export default Prospects;
